<template>
  <div v-if="!!currentTourDayItem" class="h-100">
    <v-loader v-if="tourDayItemResourcesStatus === 'loading'" />

    <form v-else id="tour-day-item-resource-update" class="d-flex flex-column justify-content-between h-100">
      <div>
        <template
          v-if="
            currentTourDayItem.caseDayItem.type !== 'general_information' &&
            currentTourDayItem.caseDayItem.type !== 'stage_information'
          "
        >
          <fieldset>
            <legend class="visually-hidden">{{ $t("tourBuilder.searchTourDayItemResource") }}</legend>

            <div class="gap-16 mb-16 d-flex">
              <div class="fw-medium">{{ $t("general.shared.supplier") }}:</div>
              <div>{{ supplierName }}</div>
            </div>

            <div class="resources-container form-outline">
              <div class="d-flex justify-content-between">
                <label class="label">
                  <span>{{ $t("general.shared.products") }}</span>
                </label>

                <label class="label me-20 bg-light">
                  <v-button
                    class="p-0 mx-4 btn-rates"
                    variant="icon"
                    @click="openOffCanvas('the-supplier-details-rates-off-canvas')"
                  >
                    <v-icon class="px-0" icon="euro" />
                  </v-button>

                  <v-button
                    class="p-0 mx-4 btn-rates"
                    variant="icon"
                    @click="openOffCanvas('the-supplier-details-offcanvas')"
                  >
                    <v-icon class="px-0" icon="pencil" />
                  </v-button>

                  <v-button class="p-0 mx-4 btn-rates" variant="icon" @click="loadSupplierSelected">
                    <v-icon class="px-0" icon="refresh" />
                  </v-button>
                </label>
              </div>

              <div
                v-if="!!currentTourDayItem.resourcePool && currentTourDayItem.resourcePool.length > 0"
                class="form-control"
              >
                <div class="scrollable-list">
                  <div
                    v-for="resource in currentTourDayItem.resourcePool"
                    :key="resource.id"
                    class="py-4 resource-item d-flex justify-content-between align-items-center"
                  >
                    <v-checkbox
                      :id="`tour-day-item-create-resource-${resource.id}`"
                      v-model="resource.check"
                      :for-key="`tour-day-item-create-resource-${resource.id}`"
                      :label="resource.name"
                      :isDisabled="isSold"
                      class="form-check-inline me-32"
                      label-class="ms-0"
                      @click.prevent="setResource(resource)"
                    />

                    <div v-if="!!checkExistResource(resource.id)" class="d-flex">
                      <v-button
                        v-if="checkExistResource(resource.id)?.quantity > 1"
                        class="p-0"
                        size="sm"
                        variant="icon"
                        :disabled="isSold"
                        @click="removeResource(resource)"
                      >
                        <v-icon icon="remove" size="sm" />
                      </v-button>

                      <div class="mx-8">{{ checkExistResource(resource.id)?.quantity }}</div>

                      <v-button class="p-0" size="sm" variant="icon" :disabled="isSold" @click="addResource(resource)">
                        <v-icon icon="circle-plus" size="sm" />
                      </v-button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div
              v-if="currentTourDayItem.caseDayItem.has_error"
              class="mt-16 p-12 border rounded bg-light"
            >
              <div
                v-if="currentTourDayItem.caseDayItem.error_remark"
                class="mb-8 text-sm text-danger"
              >
                {{ currentTourDayItem.caseDayItem.error_remark }}
              </div>
              <v-checkbox
                :id="`handled-by-admin-${currentTourDayItem.caseDayItem.id}`"
                v-model="handledByAdmin"
                :for-key="`handled-by-admin-${currentTourDayItem.caseDayItem.id}`"
                :is-disabled="isClearing || isSold"
                :label="$t('tourBuilder.roomHandledByAdmin')"
                @update:modelValue="handleItemHandledByAdmin"
              />
            </div>
          </fieldset>
          <fieldset>
            <div class="pb-24 mb-24 search-separator-container">
              <div class="mb-16 separator-text-container" />
            </div>

            <legend class="visually-hidden">{{ $t("tourBuilder.tourDayItemResourceDates") }}</legend>
            <div v-if="resourcesSelected.length > 0">
              <template
                v-for="resource in resourcesSelected"
                :key="`resource-${resource.data.id}${resource.data.roomTypeSlug}`"
              >
                <div
                  :id="`accordionSupplierResource${resource.data.id}${resource.data.roomTypeSlug}`"
                  :class="['mb-16 border accordion', { 'is-sold': isSold }]"
                >
                  <div class="mb-0 supplier-resource-details accordion-item">
                    <div
                      :id="`headingSupplierResource${resource.data.id}${resource.data.roomTypeSlug}`"
                      class="accordion-header"
                    >
                      <button
                        :aria-controls="`collapseSupplierResource${resource.data.id}${resource.data.roomTypeSlug}`"
                        aria-expanded="false"
                        class="p-8 accordion-button collapsed"
                        :data-bs-toggle="isSold ? null : 'collapse'"
                        :data-bs-target="
                          isSold ? null : `#collapseSupplierResource${resource.data.id}${resource.data.roomTypeSlug}`
                        "
                        :disabled="isSold"
                        type="button"
                      >
                        <span class="d-flex flex-column">
                          <span class="fw-medium pe-80" v-if="resource.data.roomTypeSlug !== ''">
                            {{ resource.data.name }}
                          </span>

                          <span class="fw-medium pe-80" v-else>
                            {{ resource.data.roomTypeSlug }}
                            <span style="color: red">(Remove this and add appropriate room type)</span>
                          </span>

                          <span v-if="algo" class="text-xs ms-4">
                            {{ $t("general.shared.pax") }}:
                            {{ resourceItemTotalResourcePax(resource.data.id) }}
                            -
                            {{ $t("general.shared.quantityShort") }}:
                            {{ resource.quantity || resource.tourDayItemResources.length }}
                          </span>
                        </span>
                      </button>
                    </div>

                    <div
                      :id="`collapseSupplierResource${resource.data.id}${resource.data.roomTypeSlug}`"
                      :aria-labelledby="`headingSupplierResource${resource.data.id}${resource.data.roomTypeSlug}`"
                      :data-bs-parent="`#accordionSupplierResource${resource.data.id}${resource.data.roomTypeSlug}`"
                      class="accordion-collapse collapse"
                    >
                      <div class="p-8 accordion-body">
                        <div
                          class="d-flex justify-content-between align-items-center pe-8"
                          v-if="!shouldHideCheckbox(resource)"
                        >
                          <v-checkbox
                            v-model="groupByMode[resource.data.id]"
                            label="Group By"
                            class="mt-8 text-nowrap"
                            @click.prevent="toggleGroupByMode(resource.data.id)"
                          />
                          <v-button
                            class="btn-icon p-4 rounded-circle"
                            v-if="groupByMode[resource.data.id]"
                            size="lg"
                            variant="gradient"
                            @click="openPaxSettings(resource)"
                          >
                            <v-icon icon="user-settings" size="sm" />
                          </v-button>
                        </div>
                        <template v-if="groupByMode[resource.data.id]">
                          <div
                            v-for="(tourDayItemResource, index) in resourceItemGroupResource(resource)"
                            :key="`tour-day-item-resource-${index}`"
                            class="p-12 mb-8 border rounded-xs gx-8"
                          >
                            <hr v-if="index > 0" class="item-form-separator my-8" />
                            <div class="d-flex justify-content-between align-items-center">
                              <div
                                class="text-sm fw-medium"
                                :class="{ 'fw-bold text-md': tourDayItemResource.id.length > 1 }"
                              >
                                {{ resource.data.name }}
                              </div>
                              <div
                                class="text-sm fw-medium"
                                :class="{ 'fw-bold text-md': tourDayItemResource.id.length > 1 }"
                              >
                                {{ $t("general.shared.quantityShort") }}:{{ tourDayItemResource.id.length }}
                              </div>
                              <!-- TODO: Dissucs about id -->
                              <!-- <v-button
                                  size="sm"
                                  variant="icon"
                                  @click="removeTourDayItemResource(tourDayItemResource.id, true)"
                                >
                                  <v-icon icon="trash" size="sm" />
                                </v-button> -->
                            </div>
                            <the-tour-day-item-details-resource-form
                              v-if="!!tourDayItemResource"
                              :agency="agency"
                              :client-tour-id="clientTourId"
                              :data="tourDayItemResource"
                              :index="index"
                              :init-date="currentTourDayItem?.day_date"
                              :status="status"
                              :tour-day-id="currentTourDayItem?.caseDayItem?.case_day_id"
                              :tour-day-item-id="currentTourDayItem?.caseDayItem?.item_data?.id"
                              :tour-id="tourId"
                              :type="currentTourDayItem?.caseDayItem?.item_data?.type"
                              :group-by-mode="groupByMode[resource.data.id]"
                              :update-view="true"
                              @refresh="refresh"
                              @saved="setItemResourcePax(tourDayItemResource)"
                              @refresh-resource-data="tourDayItemResource = $event"
                              @emit-reload-tour="
                                $emit('emitReloadTour');
                                $emit('openItemDetailsOffCanvas', $event);
                              "
                              version="new"
                            />

                            <div class="mb-16 search-separator-container pb16">
                              <div class="separator-text-container" />
                            </div>

                            <the-tour-day-item-details-resource-reservation-form
                              :agency="agency"
                              :client-tour-id="clientTourId"
                              :resource="tourDayItemResource"
                              :tour-day-id="currentTourDayItem?.case_day_id"
                              :tour-day-item-id="currentTourDayItem?.caseDayItem?.item_data?.id"
                              :tour-id="tourId"
                              @emit-reload-tour="$emit('emitReloadTour')"
                              version="new"
                            />
                          </div>
                        </template>
                        <template v-else>
                          <div
                            v-for="(tourDayItemResource, index) in resource.tourDayItemResources"
                            :key="`tour-day-item-resource-${tourDayItemResource.id}`"
                            class="p-12 mb-8 border rounded-xs gx-8"
                          >
                            <hr v-if="index > 0" class="item-form-separator my-8" />
                            <div class="d-flex justify-content-between align-items-center">
                              <div class="text-sm fw-medium">
                                {{ resource.data.name }}
                                <span v-if="resource.tourDayItemResources.length > 1" class="text-xs fw-normal">
                                  (#{{ index + 1 }})
                                </span>
                              </div>

                              <div>
                                <v-button
                                  class="btn-icon p-4 rounded-circle"
                                  size="lg"
                                  v-if="shouldHideCheckbox(resource) || !groupByMode[resource.data.id]"
                                  variant="gradient"
                                  @click="openPaxSettings(resource, tourDayItemResource)"
                                >
                                  <v-icon icon="user-settings" size="sm" />
                                </v-button>

                                <v-button
                                  size="sm"
                                  variant="icon"
                                  @click="removeTourDayItemResource(tourDayItemResource.id, true)"
                                >
                                  <v-icon icon="trash" size="sm" />
                                </v-button>
                              </div>
                            </div>
                            <the-tour-day-item-details-resource-form
                              v-if="!!tourDayItemResource"
                              :agency="agency"
                              :days-count="daysCount"
                              :client-tour-id="clientTourId"
                              :data="tourDayItemResource"
                              :index="tourDayItemResource.id"
                              :init-date="currentTourDayItem?.day_date"
                              :status="status"
                              :tour-day-id="currentTourDayItem?.caseDayItem?.case_day_id"
                              :tour-day-item-id="currentTourDayItem?.caseDayItem?.item_data?.id"
                              :tour-id="tourId"
                              :type="currentTourDayItem?.caseDayItem?.item_data?.type"
                              :update-view="true"
                              :group-by-mode="groupByMode[resource.data.id]"
                              @refresh="refresh"
                              @saved="setItemResourcePax(tourDayItemResource)"
                              @refresh-resource-data="tourDayItemResource = $event"
                              @emit-reload-tour="
                                $emit('emitReloadTour');
                                loadTourData();
                              "
                              version="new"
                            />
                          </div>
                        </template>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </fieldset>
        </template>
        <template v-else>
          <fieldset>
            <div class="py-8 selected d-center">
              <div class="mb-20 detail-title d-flex justify-content-between align-items-center">
                <div class="fw-medium">{{ $t("resource.basicInformation") }}</div>

                <v-button class="p-4 btn-edit" variant="icon" @click="openOffCanvas('the-resource-details-offcanvas')">
                  <v-icon icon="pencil" />
                </v-button>
              </div>
              <template v-for="(lang, index) in stageData?.translations" :key="index">
                <div class="py-4 text-sm d-flex" v-if="currentLanguage === lang?.locale">
                  <span class="me-16 fw-medium">{{ $t("general.shared.title") }}: </span>
                  <span>{{ lang?.title }}</span>
                </div>
              </template>
              <div class="py-4 text-sm d-flex">
                <span class="me-16 fw-medium">{{ $t("general.shared.location") }}: </span>
                <span>
                  {{ stageData?.city }}
                  <v-icon v-if="!!stageData?.destination" icon="arrow-right" size="xs" space="px-8" />
                  {{ stageData?.destination }}
                </span>
              </div>
              <div class="py-4 text-sm d-flex" v-if="!!stageData?.types">
                <span class="me-16 fw-medium">{{ $t("general.shared.types") }}: </span>
                <span>{{ JSON.parse(stageData?.types)[0] }}</span>
              </div>
              <div class="py-4 text-sm d-flex">
                <span class="info-title fw-medium">{{ $t("general.shared.distanceKm") }} : </span>
                <span v-if="stageData?.stageLength">{{ stageData?.stageLength }}</span>
                <span v-else>-</span>
              </div>
              <template v-for="(lang, index) in stageData?.translations" :key="index">
                <div class="py-4 text-sm d-flex" v-if="currentLanguage === lang?.locale">
                  <span class="me-16 fw-medium">{{ $t("general.shared.description") }}: </span>
                  <span v-html="lang?.description" />
                </div>
              </template>
              <div class="mb-8 search-separator-container">
                <div class="separator-text-container">
                  <span class="text-sm separator-text">{{ $t("general.shared.links") }}</span>
                </div>
              </div>
              <div v-if="stageData?.links" class="py-4 text-sm d-flex flex-column">
                <span class="me-16 fw-medium">{{ $t("general.shared.links") }}: </span>
                <template v-for="url in JSON.parse(stageData?.links)" :key="url">
                  <a :href="url" class="resource-link btn-link" rel="noopener noreferrer" target="_blank">
                    {{ url }}
                  </a>
                </template>
              </div>
            </div>
          </fieldset>
        </template>
      </div>
    </form>
    <!-- TODO: Temporary comment this code block -->
    <!-- <fieldset class="mb-24">
        <div class="form-outline">
          <label class="label">
            <span>{{ $t("general.shared.supplier_observations") }}</span>
          </label>
        </div>
        <v-html-editor
          :id="`tour-day-item-resource-supplier-update-observations`"
          ref="tour-day-item-resource-supplier-observations-ref"
          :label="$t('general.shared.supplier_observations')"
          :value="observations"
          :height="'250px'"
          readonly="true"
          disabled="disabled"
        />
      </fieldset> -->

    <the-supplier-details-rates-off-canvas
      v-if="offCanvasToShow === 'the-supplier-details-rates-off-canvas'"
      :supplier-id="supplier.value.id"
      @closed="closeOffCanvas"
    />

    <the-supplier-create-offcanvas
      v-if="offCanvasToShow === 'the-supplier-create-offcanvas'"
      @closed="closeOffCanvas"
      @reload-suppliers="closeOffCanvas"
      @open-details="supplier = $event"
    />

    <the-supplier-details-offcanvas
      v-if="offCanvasToShow === 'the-supplier-details-offcanvas'"
      :is-archived="false"
      :is-double-off-canvas="false"
      :supplier-data="supplier.value"
      @closed="
        closeOffCanvas();
        loadSupplierSelected();
      "
    />
    <the-resource-details-offcanvas
      v-if="offCanvasToShow === 'the-resource-details-offcanvas'"
      :is-archived="false"
      :resource-data="stageData"
      tour-builder="tourBuilder"
      @closed="closeOffCanvas()"
      @reload-resources="
        closeOffCanvas();
        $emit('emitReloadTour');
      "
    />

    <the-case-day-item-pax-selection
      v-if="showPaxSelection"
      :selected-resource="selectedResource"
      :case-day-item-resources="caseDayItemResources"
      :supplier-resource-id="supplierResourcesId"
      :agency="agency"
      :case-id="props.clientTourId"
      :case-day-id="currentTourDayItem.caseDayItem?.case_day_id"
      :item-id="itemId"
      @closed="showPaxSelection = false"
      @refresh="refresh"
    />
  </div>
</template>

<script lang="ts" setup name="TheCaseDayItemDetailsUpdateForm">
import { computed, getCurrentInstance, onMounted, reactive, ref, watch } from "vue";
import { VCheckbox } from "@uniqoders/form";
import { storeToRefs } from "pinia";
import VIcon from "@/components/vendor/basic/icon/VIcon.vue";
import VLoader from "@/components/vendor/loader/VLoader.vue";
import Supplier from "@/api/objects/Supplier";
import { useUserStore } from "@/stores/user";
import api from "@/api";
import VButton from "@/components/vendor/basic/button/VButton.vue";
import TheTourDayItemDetailsResourceForm from "@/components/tour/TheTourDayItemDetailsResourceForm.vue";
import TheSupplierDetailsRatesOffCanvas from "@/components/supplier/parts/TheSupplierDetailsRatesOffCanvas.vue";
import useOffCanvasUtils from "@/helpers/OffCanvasUtils";
import TheSupplierCreateOffcanvas from "@/components/supplier/TheSupplierCreateOffcanvas.vue";
import TheSupplierDetailsOffcanvas from "@/components/supplier/TheSupplierDetailsOffcanvas.vue";
import TheResourceDetailsOffcanvas from "@/components/resource/TheResourceDetailsOffcanvas.vue";
import TheCaseDayItemPaxSelection from "@/components/case/TheCaseDayItemPaxSelection.vue";
import { useAppStore } from "@/stores/app";
import { useI18n } from "vue-i18n";
import { POSITION, useToast } from "vue-toastification";

const props = defineProps({
  agency: {
    type: String,
    required: true,
  },
  clientTourId: {
    type: Number,
    required: true,
  },
  tourId: {
    type: Number,
    required: true,
  },
  status: {
    type: String,
    required: true,
  },
  daysCount: {
    type: Number,
    required: true,
  },
  isSold: {
    type: Boolean,
    required: true,
  },
});

// ✅ define emits
const emit = defineEmits(["closed", "emitReloadTour", "deletedItemResource", "openItemDetailsOffCanvas"]);

const { t } = useI18n();
const toast = useToast();
const userStore = useUserStore();
const appStore = useAppStore();
const instance = getCurrentInstance();
const $modal = instance?.appContext.config.globalProperties.$modal;
const { offCanvasToShow, openOffCanvas, closeOffCanvas } = useOffCanvasUtils();

const supplierName = ref<string>("");
const tourDayItemResourcesStatus = ref<string>("loading");
const algo = ref<boolean>(true);
const currentLanguage = ref(localStorage.getItem("language"));
const observations = ref<string>("");
const showPaxSelection = ref<boolean>(false);
const selectedResource = ref<any>({});
const itemId = ref<number>(0);
const caseDayItemResources = ref<any[]>([]);
const supplierResourcesId = ref<number>(0);
const handledByAdmin = ref<boolean>(false);
const isClearing = ref<boolean>(false);

// Reactive objects/arrays
const supplier = reactive<Supplier>({} as Supplier);
const resourcesSelected = ref<Record<string, any>[]>([]);
const groupByMode = reactive<{ [key: number]: boolean }>({});

const { currentTourDayItem } = storeToRefs(userStore);
const stageData = computed(() => currentTourDayItem.value?.caseDayItem?.item_data);

onMounted(async () => {
  setTourDayItemData();
  await loadSupplierSelected();

  resourcesSelected.value?.forEach((resource) => {
    if (resource?.tourDayItemResources?.length > 1) {
      groupByMode[resource?.data?.id] = true;
    }
  });
});

watch(currentTourDayItem, () => {
  handledByAdmin.value = false;
});

const setTourDayItemData = () => {
  if (currentTourDayItem.value) {
    resourcesSelected.value = [];

    // Map caseDayItemResources
    currentTourDayItem.value?.caseDayItem?.case_day_item_resources?.forEach((resource: any) => {
      const targetId = resource.resource_id || resource.supplier_resource_id;
      const resourceExist = checkExistResource(targetId);
      if (!!resourceExist) {
        resourceExist.tourDayItemResources.push(resource);
        resourceExist.quantity += 1;
      } else if (resource.supplier_resource) {
        resourcesSelected.value.push({
          data: {
            id: resource.id,
            supplier_resource_id: resource.supplier_resource_id,
            name: resource.supplier_resource?.name,
            selectedPax: resource.paxes,
            roomTypeSlug: resource.supplier_resource?.type,
          },
          name: resource.supplier_resource.name,
          id: targetId,
          quantity: resource.supplier_resource.quantity || 1,
          tourDayItemResources: [resource],
          paxes: currentTourDayItem.value?.paxes,
        });
      }
    });
    tourDayItemResourcesStatus.value = "loaded";
  }
};

const loadTourData = async () => {
  const data = JSON.parse(localStorage.getItem("currentTourDayItem") as string);
  emit("openItemDetailsOffCanvas", data);
};

const mapResources = () => {
  currentTourDayItem.value?.resourcePool?.forEach((resource: any) => {
    const resourceExist = checkExistResource(resource.id);

    if (resource.type === "hotel") {
      resource.check = !!resourceExist;
    } else {
      resource.check = !!resourceExist;
    }
  });
};

const loadSupplierSelected = async () => {
  if (!!currentTourDayItem.value) {
    supplier.value = currentTourDayItem.value?.caseDayItem?.item_data;
    supplierName.value =
      supplier.value.type !== "stage_information" && supplier.value.type !== "general_information"
        ? supplier.value?.name || "-"
        : supplier.value?.title || "";
    mapResources();
  }

  const data = resourcesSelected.value?.flatMap((resourceSelected: any) =>
    resourceSelected?.tourDayItemResources
      ?.filter((resource: any) => resource?.observations !== null)
      ?.map((resource: any) => resource?.observations)
  );

  observations.value = data?.[0] || "";
};

const handleItemHandledByAdmin = async (checked: boolean) => {
  if (!checked || !currentTourDayItem.value) return;

  isClearing.value = true;

  try {
    await api.case.caseDayItemClearNotes(
      props.agency,
      props.clientTourId,
      currentTourDayItem.value.caseDayItem.case_day_id,
      currentTourDayItem.value.caseDayItem.id
    );

    toast.success(t("general.shared.savedChanges"));

    await userStore.setCurrentCaseDayItem({
      agency: props.agency,
      caseId: props.clientTourId,
      caseDayId: currentTourDayItem.value.caseDayItem.case_day_id,
      caseDayItemId: currentTourDayItem.value.caseDayItem.id,
    });

    emit("emitReloadTour");
  } catch (e: any) {
    handledByAdmin.value = false;
    toast.error(e.response?.data?.message || t("general.shared.error"));
  } finally {
    isClearing.value = false;
  }
};

const setResource = (resource: any) => {
  if (!resource.check) {
    addResource(resource);
    resource.check = true;
  } else {
    deleteModalTourDayItemResource(resource);
  }
};

const removeResource = async (resource: any) => {
  const resourceExist = checkExistResource(resource.id);
  if (resourceExist) {
    await removeTourDayItemResource(resourceExist.tourDayItemResources[0].id, true);
  }
};

const addResource = async (resource: any) => {
  if (!currentTourDayItem.value) return;

  let pax;
  const dayPaxCount = currentTourDayItem.value?.paxes?.length ?? 1;
  let qtyToAdd = 1;

  switch (resource.type) {
    case "hotel":
      if (resource?.name?.includes("Single")) pax = 1;
      else if (resource?.name?.includes("Double")) pax = 2;
      else if (resource?.name?.includes("Triple")) pax = 3;
      else pax = dayPaxCount;
      break;

    case "transfer":
      pax = dayPaxCount;
      break;
      
    default:
      pax = 1;
      // If we're clicking + and there are no instances of it yet, we might want dayPaxCount, 
      // but if we are adding 1 explicit extra, we should just add 1.
      // In update mode, the user expects clicking + to add 1 unit.
      // However, to be consistent with creation mapping, if there are 0, add dayPaxCount.
      const existingCount = currentTourDayItem.value.caseDayItem.case_day_item_resources?.filter((r: any) => r.supplier_resource_id === resource.id).length || 0;
      if (existingCount === 0) qtyToAdd = dayPaxCount;
      break;
  }

  const resourcesPayload = [];
  for (let i = 0; i < qtyToAdd; i++) {
    resourcesPayload.push({
      id: resource.id,
      pax,
      nights: 1,
    });
  }

  try {
    await api.case.caseDayItemUpdate(
      props.agency,
      props.clientTourId,
      currentTourDayItem.value.caseDayItem.case_day_id,
      currentTourDayItem.value.caseDayItem.id,
      {
        type: currentTourDayItem.value.caseDayItem.model_type,
        model_id: currentTourDayItem.value.caseDayItem.model_id,
        resources: resourcesPayload,
      }
    );

    toast.success(t("general.shared.savedChanges"), {
      position: POSITION.BOTTOM_RIGHT,
    });

    const oldChecks = currentTourDayItem.value.resourcePool?.reduce(
      (acc: { [x: string]: any }, r: { id: string | number; check: any }) => {
        acc[r.id] = r.check;
        return acc;
      },
      {}
    );
    const item = {
      agency: props.agency,
      caseId: props.clientTourId,
      caseDayId: currentTourDayItem.value.caseDayItem.case_day_id,
      caseDayItemId: currentTourDayItem.value.caseDayItem.id,
    };

    await userStore.setCurrentCaseDayItem(item);

    currentTourDayItem.value.resourcePool?.forEach((r: { check: any; id: string | number }) => {
      r.check = oldChecks[r.id] || false;
    });

    setTourDayItemData();
  } catch (e: any) {
    console.error(e);
    toast.error(e?.response?.data?.message || t("general.shared.somethingWentWrong"));
  }
};

const refresh = async () => {
  const item = {
    agency: props.agency,
    caseId: props.clientTourId,
    caseDayId: currentTourDayItem.value.caseDayItem.case_day_id,
    caseDayItemId: currentTourDayItem.value.caseDayItem.id,
  };

  await userStore.setCurrentCaseDayItem(item);
  setTourDayItemData();
  mapResources();
};

const checkExistResource = (resourceId: number | any) => {
  return resourcesSelected.value.find((resourceAux) => resourceAux.id === resourceId);
};

/** ✅ Calculate total pax */
const resourceItemTotalResourcePax = (resourceId: number) => {
  const resource = resourcesSelected.value.find((r) => r.data.id === resourceId);
  if (resource && resource.tourDayItemResources.length > 0) {
    let totalPax = 0;
    resource.tourDayItemResources.forEach((tourDayItemResource: any) => {
      const resourceType = tourDayItemResource?.resource?.type || tourDayItemResource?.supplier_resource?.type;
      if (resourceType === "transfer" || resourceType === "hotel") {
        const paxCount = tourDayItemResource?.pax ?? tourDayItemResource?.paxes?.length ?? 0;
        totalPax += Number(paxCount) || 0;
      } else {
        totalPax += 1;
      }
    });
    return totalPax;
  }
  return 0;
};

/** ✅ Toggle group-by mode */
const toggleGroupByMode = (resourceId: number) => {
  if (typeof groupByMode[resourceId] === "undefined") {
    groupByMode[resourceId] = true;
  } else {
    groupByMode[resourceId] = !groupByMode[resourceId];
  }
};

/** ✅ Group resource items */
const resourceItemGroupResource = (resourceData: any) => {
  const resource = resourcesSelected.value.find((r) => r.data.id === resourceData.data.id);

  const grouped = resource?.tourDayItemResources?.reduce((acc: any, cur: any) => {
    const resourceId = cur?.resource?.id ?? cur?.supplier_resource_id;
    const roomType = cur?.resource?.resource_type ?? cur?.supplier_resource?.resource_type ?? cur?.data?.roomTypeSlug ?? '';
    const paxCount = cur?.paxes?.length ?? cur?.pax ?? 0;
    // Nights is intentionally excluded from the grouping key: all resources on
    // the same item share the same nights (cascaded by the backend), so splitting
    // by nights would create phantom separate groups and cause stale-night bugs.
    const key = `${paxCount}-${cur.supplier_observation ?? ''}-${cur.client_observation ?? ''}-${roomType}`;

    if (!acc[resourceId]) acc[resourceId] = {};

    const matchingResourceData = resourceData.tourDayItemResources.find((data: any) => {
      const dataPaxCount = data?.paxes?.length ?? data?.pax ?? 0;
      const dataRoomType = data?.resource?.resource_type ?? data?.supplier_resource?.resource_type ?? '';
      return (
        paxCount === dataPaxCount &&
        (cur.supplier_observation ?? '') === (data.supplier_observation ?? '') &&
        (cur.client_observation ?? '') === (data.client_observation ?? '') &&
        roomType === dataRoomType
      );
    });

    if (matchingResourceData) {
      if (!acc[resourceId][key]) {
        acc[resourceId][key] = { ...cur, id: [cur.id] };
      } else {
        acc[resourceId][key].id.push(cur.id);
      }
    }
    return acc;
  }, {});

  const result: any[] = [];
  for (const resourceId in grouped) {
    for (const key in grouped[resourceId]) {
      result.push(grouped[resourceId][key]);
    }
  }

  return result.reverse();
};

/** ✅ Hide checkbox conditionally */
const shouldHideCheckbox = (resource: any) => {
  const groupedResources = resourceItemGroupResource(resource);
  const allSingleId = groupedResources.every((item) => item.id.length === 1);
  if (allSingleId) {
    groupByMode[resource.data.id] = false;
    return true;
  }
  return false;
};

/** ✅ Delete resource (with modal) */
const deleteModalTourDayItemResource = async (resource: any) => {
  if (currentTourDayItem.value) {
    const resourceExist = resourcesSelected.value.find((r) => r.data.supplier_resource_id === resource.id);
    await $modal.delete({
      title: t("tourBuilder.deleteTourDayItemResourceTitle"),
      text: t("tourBuilder.deleteTourDayItemResourceText", {
        resource: resourceExist?.name,
        // date: settings.formatDateTime(currentTourDayItem.value.tourDay.date, settings.formatDate),
      }),
      deleteButtonText: t("general.button.delete"),
      rightButtonClasses: "btn-tertiary",
      deleteButtonCallback: () => doDeleteAllTourDayItemResources(resourceExist, resource),
    });
  }
};

/** ✅ Delete all resources */
const doDeleteAllTourDayItemResources = async (resource: any, resourceOriginal: any) => {
  if (currentTourDayItem.value) {
    for (const tourDayItemResource of resource.tourDayItemResources) {
      await removeTourDayItemResource(tourDayItemResource.id, false);
    }

    resourceOriginal.check = false;

    const item = {
      agency: props.agency,
      caseId: props.tourId,
      caseDayId: currentTourDayItem.value.caseDayItem.case_day_id,
      caseDayItemId: currentTourDayItem.value.caseDayItem.id,
    };

    emit("deletedItemResource");

    await userStore.setCurrentCaseDayItem(item);

    setTourDayItemData();
    mapResources();
  }
};

/** ✅ Remove tour day item resource */
const removeTourDayItemResource = async (tourDayItemResource: number, refreshCurrentTourDayItem = true) => {
  if (currentTourDayItem.value) {
    try {
      await api.case.caseDayItemRemoveResource(
        props.agency,
        props.tourId,
        currentTourDayItem.value.caseDayItem.case_day_id,
        currentTourDayItem.value.caseDayItem.id,
        tourDayItemResource
      );

      toast.success(t("general.shared.savedChanges"));

      if (refreshCurrentTourDayItem) {
        const item = {
          agency: props.agency,
          caseId: props.tourId,
          caseDayId: currentTourDayItem.value.caseDayItem.case_day_id,
          caseDayItemId: currentTourDayItem.value.caseDayItem.id,
        };

        emit("deletedItemResource");
        await userStore.setCurrentCaseDayItem(item);
        setTourDayItemData();
        mapResources();
      }
    } catch (e: any) {
      console.error(e);
      toast.error(e.response?.data?.message || "Error removing resource");
    }
  }
};

const openPaxSettings = (resource: any, caseDayItemResource?: any) => {
  const data = {
    data: {
      id: caseDayItemResource?.id,
      supplier_resource_id: caseDayItemResource?.supplier_resource_id,
      name: caseDayItemResource?.supplier_resource?.name,
      selectedPax: caseDayItemResource?.paxes,
      roomTypeSlug: caseDayItemResource?.supplier_resource?.type,
    },
    name: resourcesSelected.value[0].name,
    id: resourcesSelected.value[0].id,
    quantity: resourcesSelected.value[0].quantity,
    tourDayItemResources: resourcesSelected.value[0].tourDayItemResources,
    paxes: resourcesSelected.value[0].paxes,
  };

  showPaxSelection.value = true;
  itemId.value = currentTourDayItem?.value?.caseDayItem?.id;
  selectedResource.value = resource.paxes;
  // Always pass ALL resources so the pax selection modal can detect
  // pax already assigned to another room/resource within the same item
  caseDayItemResources.value = resourcesSelected.value;
  supplierResourcesId.value = caseDayItemResource.supplier_resource_id ?? resource.data.supplier_resource_id;
};
</script>

<style lang="scss" scoped>
.accordion-item {
  background-color: transparent;
}

.accordion .accordion-item .accordion-button {
  font-size: 1rem;
}

.accordion-button,
.accordion-button:not(.collapsed) {
  background-color: transparent;
}

.scrollable-list {
  max-height: calc(5 * 40px);
  overflow-y: auto;
}

.is-sold .accordion-button,
.is-sold .accordion-button:not(.collapsed) {
  background-color: transparent;
}

.is-sold .accordion-button::before,
.is-sold .accordion-button::after {
  display: none;
}

.item-form-separator {
  border: 0;
  border-top: 1px solid #000;
  margin: 0;
  opacity: 1;
}
</style>
